import React, { ReactText } from 'react';
import Player from '../models/Player';

export enum Type {
    POINTS_GAMES,
    POINTS_ELIGIBLE,
    WINS_GAMES,
    WINS,
    POINTS,
    GAMES,
    ELIGIBLE,
    ELIGIBLE_GAMES,
}

const labels = {
    [Type.POINTS_GAMES]: "Points/Games",
    [Type.POINTS_ELIGIBLE]: "Points/Eligible",
    [Type.WINS_GAMES]: "Wins/Games",
    [Type.WINS]: "Wins",
    [Type.POINTS]: "Points",
    [Type.GAMES]: "Games",
    [Type.ELIGIBLE]: "Eligible",
    [Type.ELIGIBLE_GAMES]: "Eligible/Games",
}

const calculators = {
    [Type.POINTS_GAMES]: (player: Player) => player.metric = player.points / player.games,
    [Type.POINTS_ELIGIBLE]: (player: Player) => player.metric = player.points / player.eligible,
    [Type.WINS_GAMES]: (player: Player) => player.metric = player.wins / player.games,
    [Type.WINS]: (player: Player) => player.metric = player.wins,
    [Type.POINTS]: (player: Player) => player.metric = player.points,
    [Type.GAMES]: (player: Player) => player.metric = player.games,
    [Type.ELIGIBLE]: (player: Player) => player.metric = player.eligible,
    [Type.ELIGIBLE_GAMES]: (player: Player) => player.metric = Math.round(player.eligible / player.games),
}

const compareMetric = (a: Player, b: Player): number => {
    if (a.metric > b.metric) {
        return -1;
    }

    if (a.metric < b.metric) {
        return 1;
    }

    return 0;
}

const calculateMetricRank = (players: Player[]): void => {
    players.sort(compareMetric)
    let rank = 0
    let metric: number | null = null

    players.forEach(player => {
        if (metric !== player.metric) {
            rank++
            metric = player.metric
        }

        player.rank = rank
    });
}

export interface Formatter {
    (metric: ReactText): string
}

export const formatRatio: Formatter = (metric: ReactText): string => {
    return Number(metric).toFixed(3)
}

const formatPercent: Formatter = (metric: ReactText): string => {
    return (Number(metric) * 100).toFixed(1) + '%'
}

const formatInteger: Formatter = (metric: ReactText): string => {
    return Number(metric).toFixed(0)
}

const getFormatter = (type: Type): Formatter => {
    if (type === Type.POINTS_GAMES) {
        return formatRatio
    }

    if ([Type.POINTS_ELIGIBLE, Type.WINS_GAMES].includes(type)) {
        return formatPercent
    }

    return formatInteger
}

interface Props {
    players: Player[]
    setFormatter: Function
    setPlayers: Function
    setLockText: Function
    rankIsLocked: boolean
}

export const execute = ({ players, setFormatter, setPlayers, setLockText, rankIsLocked }: Props, type: Type): void => {
    players.forEach(player => {
        calculators[type](player)
    })

    if (!rankIsLocked) {
        calculateMetricRank(players)
        setLockText(labels[type])
    }

    setFormatter(getFormatter(type))
    setPlayers([...players])
}

const Option: React.FC<{ type: Type}> = ({ type }) => {
    return <option value={type}>{labels[type]}</option>
}

const Metrics: React.FC<Props> = ({ players, setFormatter, setPlayers, setLockText, rankIsLocked }) => {
    const onChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        execute({ players, setFormatter, setPlayers, setLockText, rankIsLocked }, Number(e.target.value))
    }

    const options = Object.values(Type)
        .filter(type => !isNaN(Number(type)))
        .map(type => <Option key={type} type={type as Type} />)

    return <select onChange={onChange}>{options}</select>
}

export default Metrics
